import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Plus,
  Package,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Tag,
  Layers,
  Image as ImageIcon,
  Settings,
  Trash2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getProductById, deleteProduct } from '../api/products';
import ProductTypeBadge from '../components/ProductTypeBadge';
import StockHistoryView from '../components/StockHistoryView';
import StockAdditionModal from '../components/StockAdditionModal';
import StockAdjustmentModal from '../components/StockAdjustmentModal';
import { getProductImage } from '../utils/imageUtils';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin =
    user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'system_admin';
  const isBranchAdmin = user?.role === 'branch_admin';
  const isHigherAdmin = ['super_admin', 'system_admin', 'retail_admin'].includes(user?.role);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getProductById(id);
      // Backend returns product directly
      setProduct(response);
    } catch (err) {
      toast.error('Failed to load product details');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  const getStockStatus = (quantity, minLevel) => {
    if (quantity === 0) {
      return { label: 'Out of Stock', color: 'text-red-600', bgColor: 'bg-red-100' };
    } else if (quantity <= minLevel) {
      return { label: 'Low Stock', color: 'text-yellow-600', bgColor: 'bg-yellow-100' };
    } else {
      return { label: 'In Stock', color: 'text-green-600', bgColor: 'bg-green-100' };
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  if (loading) {
    return <LoadingSpinner className="min-h-screen" />;
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-2">Product Not Found</h2>
        <Link to="/products" className="text-blue-600 hover:text-blue-800">
          Back to Products
        </Link>
      </div>
    );
  }

  // Get the branch admin's branch stock info
  const userBranchStock =
    isBranchAdmin && product.branch_stocks
      ? product.branch_stocks.find((b) => b.branch_id === user?.current_branch_id)
      : null;

  // Calculate stock quantity to display
  const displayStock =
    isBranchAdmin && userBranchStock
      ? userBranchStock.stock_quantity
      : product.branch_stocks
        ? product.global_stock
        : product.stock_quantity;

  const stockStatus = getStockStatus(displayStock, product.min_stock_level);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6">
      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <button
          onClick={() => navigate('/products')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Products
        </button>

        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{product.name}</h1>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <span className="text-xs sm:text-sm font-mono font-semibold px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 border-2 border-yellow-600 shadow-md">
                {product.product_code}
              </span>
              <ProductTypeBadge type={product.product_type} />
              {product.is_featured && (
                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full border border-yellow-200">
                  Featured
                </span>
              )}
              {!product.is_active && (
                <span className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded-full border border-gray-200">
                  Inactive
                </span>
              )}
              {isBranchAdmin && userBranchStock && (
                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full border border-blue-200">
                  {userBranchStock.branch_name}
                </span>
              )}
            </div>
          </div>

          {/* Action Buttons - Restricted for branch_admin */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            {/* Branch admin can add stock to their branch */}
            <button
              onClick={() => setShowStockModal(true)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors min-h-[44px]"
            >
              <Plus className="w-4 h-4" />
              Add Stock
            </button>
            {/* Only higher admins can adjust/edit/delete */}
            {isHigherAdmin && (
              <>
                <button
                  onClick={() => setShowAdjustModal(true)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors min-h-[44px]"
                >
                  <Settings className="w-4 h-4" />
                  Adjust Stock
                </button>
                <button
                  onClick={() => navigate(`/products/${id}/edit`)}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors min-h-[44px]"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </button>
                <button
                  onClick={async () => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this product? This action cannot be undone.'
                      )
                    ) {
                      try {
                        await deleteProduct(id);
                        toast.success('Product deleted successfully');
                        navigate('/products');
                      } catch (error) {
                        toast.error(error.response?.data?.error || 'Failed to delete product');
                      }
                    }
                  }}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors min-h-[44px]"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-4 sm:mb-6">
        {/* Left Column - Product Image */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-4">Product Image</h3>
            {getProductImage(product.images) ? (
              <img
                src={getProductImage(product.images)}
                alt={product.name}
                className="w-full h-64 object-cover rounded-lg"
              />
            ) : (
              <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-16 h-16 text-gray-400" />
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Product Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Stock Status Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Stock Information
              {isBranchAdmin && userBranchStock && (
                <span className="text-sm font-normal text-gray-500">
                  ({userBranchStock.branch_name})
                </span>
              )}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  {isBranchAdmin
                    ? 'Your Branch Stock'
                    : product.branch_stocks
                      ? 'Total Stock'
                      : 'Current Stock'}
                </p>
                <p className="text-2xl font-bold text-gray-900">
                  {displayStock} {product.unit}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${stockStatus.bgColor} ${stockStatus.color}`}
                >
                  {stockStatus.label}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Minimum Level</p>
                <p className="text-2xl font-bold text-gray-900">
                  {product.min_stock_level} {product.unit}
                </p>
              </div>
            </div>

            {/* Per-Branch Stock Breakdown (Only for higher admins in multi-branch mode) */}
            {!isBranchAdmin && product.branch_stocks && product.branch_stocks.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Stock by Branch</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {product.branch_stocks.map((branch) => (
                    <div
                      key={branch.branch_id}
                      className={`p-3 rounded-lg border ${
                        branch.stock_quantity <= (branch.reorder_level || 5)
                          ? 'border-yellow-300 bg-yellow-50'
                          : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{branch.branch_name}</p>
                          <p className="text-xs text-gray-500">{branch.branch_code}</p>
                        </div>
                        <div className="text-right">
                          <p
                            className={`text-lg font-bold ${
                              branch.stock_quantity <= 0
                                ? 'text-red-600'
                                : branch.stock_quantity <= (branch.reorder_level || 5)
                                  ? 'text-yellow-600'
                                  : 'text-green-600'
                            }`}
                          >
                            {branch.stock_quantity}
                          </p>
                          <p className="text-xs text-gray-500">{product.unit}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Pricing Card - Restricted for branch_admin */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              {isBranchAdmin ? 'Pricing' : 'Pricing & Profit'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Branch admin only sees selling price */}
              {!isBranchAdmin && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Cost Price</p>
                  <p className="text-xl font-bold text-gray-900">
                    {formatCurrency(product.cost_price)}
                  </p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-600 mb-1">Selling Price</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(product.selling_price)}
                </p>
              </div>
              {/* Branch admin local price if available */}
              {isBranchAdmin && userBranchStock?.local_price && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Your Branch Price</p>
                  <p className="text-xl font-bold text-green-600">
                    {formatCurrency(userBranchStock.local_price)}
                  </p>
                </div>
              )}
              {!isBranchAdmin && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Profit Margin</p>
                  <p className="text-xl font-bold text-green-600">{product.profit_margin}%</p>
                </div>
              )}
            </div>
            {!isBranchAdmin && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Profit per Unit</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(product.selling_price - product.cost_price)}
                </p>
              </div>
            )}
          </div>

          {/* Categories Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5" />
              Categories
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Brand</p>
                <p className="font-medium">{product.brand_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Size</p>
                <p className="font-medium">{product.size_dimension || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Use Case</p>
                <p className="font-medium">{product.use_case_name || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Tile Type</p>
                <p className="font-medium">{product.tile_type_name || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Description */}
      {product.description && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-3">Description</h3>
          <p className="text-gray-700 whitespace-pre-wrap">{product.description}</p>
        </div>
      )}

      {/* Specifications */}
      {product.specifications && Object.keys(product.specifications).length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Layers className="w-5 h-5" />
            Specifications
          </h3>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(product.specifications).map(([key, value]) => (
              <div key={key} className="border-b border-gray-200 pb-2">
                <dt className="text-sm font-medium text-gray-600 capitalize">
                  {key.replace(/_/g, ' ')}
                </dt>
                <dd className="text-sm text-gray-900 mt-1">{value}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Stock History - Pass branch filter for branch_admin */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <StockHistoryView
          productId={id}
          branchId={isBranchAdmin ? user?.current_branch_id : null}
        />
      </div>

      {/* Stock Addition Modal */}
      <StockAdditionModal
        isOpen={showStockModal}
        onClose={() => setShowStockModal(false)}
        product={product}
        onSuccess={fetchProduct}
        branchId={isBranchAdmin ? user?.current_branch_id : null}
      />

      {/* Stock Adjustment Modal (Higher Admins Only) */}
      {isHigherAdmin && (
        <StockAdjustmentModal
          isOpen={showAdjustModal}
          onClose={() => setShowAdjustModal(false)}
          product={product}
          onSuccess={fetchProduct}
        />
      )}
    </div>
  );
};

export default ProductDetailPage;
